// Venue-search VS1.5: the new venue facts on the venue page. The basics,
// Features, What's included, Available to rent, Rules. What each row says:
// venueFactsView.js. Host text shows only as plain text (D-0051).
import { useState } from 'react';
import { FiCheck as CheckIcon, FiChevronRight as ChevronIcon } from 'react-icons/fi';
import styled from 'styled-components';

import copy from '../../../content/venuePage';
import { fieldDefinition } from '../../../taxonomy';
import { companyEmail } from '../../helpers';
import { colors, mobile, notMobile, tablet } from '../../styles';
import { basicsRows, featureRows, inventoryView, rentalsMailto, ruleRows } from './venueFactsView';

const Section = styled.section`
  margin-bottom: 32px;
  font-size: 14px;
  line-height: 20px;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 500;
  line-height: 22px;
  margin: 0 0 12px;
`;

const Rows = styled.div`
  border-top: 1px solid ${colors.lightBorder};
  max-width: 640px;
`;

const RowLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;
`;

const Term = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const Value = styled.span`
  text-align: right;
  font-weight: 500;
`;

const Row = styled.div`
  border-bottom: 1px solid ${colors.lightBorder};
`;

const Fold = styled.details`
  border-bottom: 1px solid ${colors.lightBorder};

  summary {
    list-style: none;
    cursor: pointer;
  }
  summary::-webkit-details-marker {
    display: none;
  }
  summary svg {
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
    color: ${colors.fadedText};
    transition: transform 0.15s ease;
  }
  &[open] summary svg {
    transform: rotate(90deg);
  }
`;

const SummaryValue = styled(Value)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const Split = styled.div`
  padding: 0 0 12px 12px;
  color: ${colors.semiDarkText};

  ${RowLine} {
    padding: 4px 0;
  }
  ${Value} {
    font-weight: 400;
  }
`;

const Soft = styled.div`
  font-size: 13px;
  color: ${colors.fadedText};
  margin-top: 2px;
`;

const QuestionButton = styled.button`
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid ${colors.defaultBorder};
  background: white;
  color: ${colors.defaultText};
  font-size: 12px;
  line-height: 18px;
  cursor: pointer;
`;

const DefinitionBox = styled.div`
  font-size: 13px;
  color: ${colors.fadedText};
  background-color: ${colors.lightBackground};
  border-radius: 6px;
  padding: 8px 10px;
  margin: 0 0 10px;
`;

const Grid = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px 16px;

  @media (max-width: ${tablet}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: ${mobile}) {
    grid-template-columns: minmax(0, 1fr);
  }

  li > div:first-child {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  li > div:not(:first-child) {
    margin-left: 24px;
  }
  li > div:first-child > svg {
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
    color: ${colors.primaryIcon};
  }
`;

const Items = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-width: 640px;

  li {
    padding: 10px 0;
    border-bottom: 1px solid ${colors.lightBorder};
    overflow-wrap: anywhere;
  }
  li:first-child {
    border-top: 1px solid ${colors.lightBorder};
  }
  @media (max-width: ${mobile}) {
    li[data-extra='true'] {
      display: none;
    }
  }
`;

const ItemName = styled.span`
  font-weight: 500;
`;

const TextButton = styled.button`
  margin-top: 12px;
  padding: 0;
  border: none;
  background: none;
  color: ${colors.defaultText};
  font-size: 14px;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;

  @media (min-width: ${notMobile}) {
    display: none;
  }
`;

const RentNames = styled.p`
  margin: 0 0 4px;
  font-weight: 500;
  overflow-wrap: anywhere;
`;

const AskLink = styled.a`
  display: inline-block;
  margin-top: 10px;
  color: ${colors.primaryButton};
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

// A term with a "?" that opens its dictionary definition. The parent shows
// the box under the whole row, so it never squeezes the value.
const useDefinition = (id, label) => {
  const [open, setOpen] = useState(false);
  const text = id && fieldDefinition(id);
  if (!text) return { term: label, box: null };
  const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(!open);
  };
  return {
    term: (
      <Term>
        {label}
        <QuestionButton type="button" aria-expanded={open} aria-label={copy.showDefinition(label)} onClick={onClick}>
          ?
        </QuestionButton>
      </Term>
    ),
    box: open ? <DefinitionBox>{text}</DefinitionBox> : null
  };
};

const PairLine = ({ label, value, definition }) => {
  const { term, box } = useDefinition(definition, label);
  return (
    <>
      <RowLine>
        <span>{term}</span>
        <Value>{value}</Value>
      </RowLine>
      {box}
    </>
  );
};

// One calm pattern: the total first; a tap opens the split.
const BasicsRow = ({ row }) => {
  const { term, box } = useDefinition(row.definition, row.label);
  const hasMore = (row.split && row.split.length > 0) || (row.notes && row.notes.length > 0);
  if (!hasMore) {
    return (
      <Row>
        <RowLine>
          <span>{term}</span>
          <Value>{row.total}</Value>
        </RowLine>
        {box}
      </Row>
    );
  }
  return (
    <Fold>
      <summary>
        <RowLine>
          <span>{term}</span>
          <SummaryValue>
            {row.total}
            <ChevronIcon aria-hidden="true" />
          </SummaryValue>
        </RowLine>
      </summary>
      {box}
      <Split>
        {(row.split || []).map((part) => (
          <PairLine key={part.id} label={part.label} value={part.value} definition={part.definition} />
        ))}
        {(row.notes || []).map((note) => (
          <Soft key={note}>{note}</Soft>
        ))}
      </Split>
    </Fold>
  );
};

const Feature = ({ row }) => {
  const { term, box } = useDefinition(row.definition, row.label);
  return (
    <li>
      <div>
        <CheckIcon aria-hidden="true" />
        {term}
      </div>
      {box}
      {(row.details || []).map((detail) => (
        <Soft key={detail}>{detail}</Soft>
      ))}
    </li>
  );
};

const PHONE_ITEMS = 3;

const Included = ({ items }) => {
  const [showAll, setShowAll] = useState(false);
  return (
    <Section aria-labelledby="venue-included">
      <Title id="venue-included">{copy.included.title}</Title>
      <Items>
        {items.map((item, index) => (
          <li key={index} data-extra={!showAll && index >= PHONE_ITEMS}>
            <ItemName>{item.name}</ItemName> · {item.details.join(' · ')}
            {!!item.note && <Soft>{item.note}</Soft>}
          </li>
        ))}
      </Items>
      {items.length > PHONE_ITEMS && (
        <TextButton type="button" aria-expanded={showAll} onClick={() => setShowAll(!showAll)}>
          {showAll ? copy.included.showFewer : copy.included.seeAll(items.length)}
        </TextButton>
      )}
    </Section>
  );
};

// `values`: the venue's facts, from valuesOf(venue).
const VenueFacts = ({ venue, values }) => {
  const basics = basicsRows(values);
  const features = featureRows(values, venue.amenities);
  const { included, forRent } = inventoryView(values);
  const rules = ruleRows(values);

  return (
    <>
      {basics.length > 0 && (
        <Section aria-labelledby="venue-basics">
          <Title id="venue-basics">{copy.basics.title}</Title>
          <Rows>
            {basics.map((row) => (
              <BasicsRow key={row.id} row={row} />
            ))}
          </Rows>
        </Section>
      )}
      {features.length > 0 && (
        <Section aria-labelledby="venue-features">
          <Title id="venue-features">{copy.features.title}</Title>
          <Grid>
            {features.map((row) => (
              <Feature key={row.id} row={row} />
            ))}
          </Grid>
        </Section>
      )}
      {included.length > 0 && <Included items={included} />}
      {forRent.length > 0 && (
        <Section aria-labelledby="venue-rent">
          <Title id="venue-rent">{copy.rent.title}</Title>
          <RentNames>{forRent.join(' · ')}</RentNames>
          <Soft>{copy.rent.note}</Soft>
          <AskLink href={rentalsMailto(companyEmail, venue.name || '')}>{copy.rent.ask}</AskLink>
        </Section>
      )}
      {rules.length > 0 && (
        <Section aria-labelledby="venue-rules">
          <Title id="venue-rules">{copy.rules.title}</Title>
          <Rows>
            {rules.map((row) => (
              <Row key={row.id}>
                <PairLine label={row.label} value={row.value} definition={row.definition} />
              </Row>
            ))}
          </Rows>
        </Section>
      )}
    </>
  );
};

export default VenueFacts;
